import { useCallback, useEffect, useRef } from 'react';

export const TOAST_POST_HOVER_MIN_MS = 500;

export type ToastAutoDismissEntry = {
  id: string;
  timeout: number | null | undefined;
};

type TimerState = {
  timeoutId: ReturnType<typeof setTimeout> | null;
  remainingMs: number | null;
  deadlineMs: number | null;
  isHovering: boolean;
  configuredTimeout: number;
};

/**
 * Owns auto-dismiss timers for a toast list (including overflowed/stacked-out items).
 * Hover pause/resume is per-id so visible wrappers can pause without owning the timer.
 */
export function useToastsAutoDismiss(
  toasts: ToastAutoDismissEntry[],
  onDismiss: (id: string) => void
): {
  onMouseEnter: (id: string) => void;
  onMouseLeave: (id: string) => void;
} {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const timersRef = useRef(new Map<string, TimerState>());

  const clearTimer = useCallback((state: TimerState) => {
    if (state.timeoutId != null) {
      clearTimeout(state.timeoutId);
      state.timeoutId = null;
    }
    state.deadlineMs = null;
  }, []);

  const schedule = useCallback(
    (id: string, delayMs: number) => {
      let state = timersRef.current.get(id);
      if (!state) {
        state = {
          timeoutId: null,
          remainingMs: null,
          deadlineMs: null,
          isHovering: false,
          configuredTimeout: delayMs,
        };
        timersRef.current.set(id, state);
      }

      clearTimer(state);
      if (delayMs <= 0) {
        onDismissRef.current(id);
        return;
      }

      state.remainingMs = delayMs;
      state.deadlineMs = Date.now() + delayMs;
      state.timeoutId = setTimeout(() => {
        const current = timersRef.current.get(id);
        if (current) {
          current.timeoutId = null;
          current.deadlineMs = null;
          current.remainingMs = 0;
        }
        onDismissRef.current(id);
      }, delayMs);
    },
    [clearTimer]
  );

  // Stable fingerprint so parent re-renders with a new array identity do not reset timers.
  const toastKey = toasts.map(toast => `${toast.id}:${toast.timeout ?? 'null'}`).join('|');

  useEffect(() => {
    const activeTimedIds = new Set<string>();

    for (const toast of toasts) {
      if (toast.timeout == null) {
        const existing = timersRef.current.get(toast.id);
        if (existing) {
          clearTimer(existing);
          timersRef.current.delete(toast.id);
        }
        continue;
      }

      activeTimedIds.add(toast.id);
      const existing = timersRef.current.get(toast.id);
      if (!existing) {
        timersRef.current.set(toast.id, {
          timeoutId: null,
          remainingMs: toast.timeout,
          deadlineMs: null,
          isHovering: false,
          configuredTimeout: toast.timeout,
        });
        schedule(toast.id, toast.timeout);
      } else if (existing.configuredTimeout !== toast.timeout) {
        existing.configuredTimeout = toast.timeout;
        if (!existing.isHovering) {
          schedule(toast.id, toast.timeout);
        }
      }
    }

    for (const [id, state] of timersRef.current) {
      if (!activeTimedIds.has(id)) {
        clearTimer(state);
        timersRef.current.delete(id);
      }
    }
    // toastKey captures id+timeout membership; `toasts` is read from this render closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional fingerprint sync
  }, [toastKey, schedule, clearTimer]);

  // Clear only when the list owner unmounts — not when membership fingerprints change.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const state of timers.values()) {
        if (state.timeoutId != null) {
          clearTimeout(state.timeoutId);
        }
      }
      timers.clear();
    };
  }, []);

  const onMouseEnter = useCallback(
    (id: string) => {
      const state = timersRef.current.get(id);
      if (!state) {
        return;
      }
      state.isHovering = true;
      if (state.deadlineMs != null) {
        state.remainingMs = Math.max(0, state.deadlineMs - Date.now());
      }
      clearTimer(state);
    },
    [clearTimer]
  );

  const onMouseLeave = useCallback(
    (id: string) => {
      const state = timersRef.current.get(id);
      if (!state) {
        return;
      }
      state.isHovering = false;
      const remaining = state.remainingMs ?? 0;
      schedule(id, Math.max(remaining, TOAST_POST_HOVER_MIN_MS));
    },
    [schedule]
  );

  return { onMouseEnter, onMouseLeave };
}
